/**
 * Phase 29 — "Replay the last N days" under a rule card, as one quiet row:
 * the rule as typed (saved or not), run by the engine's own judges over
 * the real sends of the last N days with the stats stored at the time —
 * on demand, zero writes. Nothing to replay before the container is saved.
 * Hidden behind SHOW_REPLAY since 23/10/2026 (client QA: "Lets hide this option").
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { History } from 'lucide-react';

import { Button, NumberField } from '@/components/ui';
import { SHOW_REPLAY } from '@/features/drop-generator/utils/flags/hidden-features';
import { getApiErrorMessage } from '@/lib/utils';

import type { ContainerDraft, DropGenPlanDetail } from '../../../../drop-generator.types';
import { INPUT_CLASS } from '../../general/fields/field-chrome';
import { replayBody, type ReplayKind, sourceOfKind } from './replay-request';
import { ReplayResult } from './ReplayResult';
import { useReplay } from './use-replay';

const DEFAULT_DAYS = 7;

const WHAT: Record<ReplayKind, string> = {
  verdict: 'What if this rule had been on? It runs, as typed, on the drops that really went out those days and shows what it would have burned or stopped.',
  scaling: 'What if this rule had been on? It runs, as typed, on the drops that really went out those days and shows how much they would have grown.',
  gateway: 'What if this rule had been on? It runs, as typed, on each gateway’s real drops of those days and shows what it would have blocked.',
  stopLoss: 'What if this stop-loss had been on? It runs, as typed, on the drops that really went out those days and shows when it would have halted.',
  hitAgain: 'What if this rule had been on? It judges, as typed, each drop that really went out those days on its own numbers and counts the ones that would have been sent again — and the SMS that would have added.',
};

interface Props {
  kind: ReplayKind;
  draft: ContainerDraft;
  plan: Pick<DropGenPlanDetail, 'countryKey' | 'recipientTimezone'>;
  /** null while the container is new — nothing to replay yet. */
  containerId: string | null;
}

export const ReplayPanel: FC<Props> = (props) => {
  if (!SHOW_REPLAY || !props.containerId) return null;
  // Phase 39 — a card on a list's or a gateway's own report cannot be replayed: no day-by-day history of it exists.
  if (sourceOfKind(props.kind, props.draft).kind !== 'drops')
    return (
      <p className="flex items-center gap-2 text-[11px] text-muted-foreground" data-testid="replay-skipped">
        <History className="h-3.5 w-3.5 shrink-0" aria-hidden />
        No replay for this card: it judges a report read live over a date range, which is not kept day by day. Switch it to today&apos;s drops to replay.
      </p>
    );
  return <ReplayLive {...props} containerId={props.containerId} />;
};

const ReplayLive: FC<Props & { containerId: string }> = ({ kind, draft, plan, containerId }) => {
  const [days, setDays] = useState(DEFAULT_DAYS);
  const replay = useReplay(plan.countryKey, containerId);
  const run = () => replay.mutate(replayBody(kind, draft, days));
  return (
    <div className="flex flex-col gap-2" data-testid="replay-panel">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <History className="h-3.5 w-3.5" aria-hidden />
        <span>Replay the last</span>
        <NumberField value={days} min={1} max={30} onChange={(v) => setDays(Math.min(30, Math.max(1, v)))} className={`${INPUT_CLASS} h-7 w-14 text-xs`} aria-label="Days to replay" />
        <span>days</span>
        <Button size="sm" variant="outline" loading={replay.isPending} onClick={run}>
          Replay
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">{WHAT[kind]} Nothing is written — a test on the past.</p>
      {replay.isError ? <p className="text-xs text-destructive">{getApiErrorMessage(replay.error)}</p> : null}
      {replay.data ? (
        <div className="duration-300 animate-in fade-in-0 slide-in-from-top-1 motion-reduce:animate-none">
          <ReplayResult result={replay.data} kind={kind} tz={plan.recipientTimezone} stale={replay.isPending} />
        </div>
      ) : null}
    </div>
  );
};
